import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Button, Dialog, Icon, Typography, Pagination } from '@equinor/eds-core-react'
import { close } from '@equinor/eds-icons'
import { baseFont } from '@/styles/layout'
import { ThumbnailDto } from '@/core/models/types'
import { getImage } from '@/api/apiService'
import { FileResultEnum } from '@/api_generated/api'
import { useInfoDialog } from '@/hooks'
import { VContainer } from '@/styles'

interface ImageProps {
    tag: string
    plant: string
    showImage: boolean
    onClose: () => void
    files: ThumbnailDto[]
    index: number | null
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max)

const ImageDisplayer: React.FC<ImageProps> = ({ tag, plant, showImage, onClose, files, index }) => {
    const [currentImage, setCurrentImage] = useState<string | null>(null)
    const [currentDescription, setCurrentDescription] = useState<string | null>(null)
    const [itemsPerPage] = useState(1)
    const [currentPageIndex, setCurrentPageIndex] = useState(() => (index ?? 0) + 1)

    const { showInfo } = useInfoDialog(
        {
            title: 'Access Required',
            content: (
                <VContainer $gap="16px">
                    <Typography>
                        You do not have permission to view this image. Please request the role “STID Read Internal” for
                        relevant plant in AccessIT to get access.
                    </Typography>
                </VContainer>
            ),
            type: 'Info',
        },
        []
    )

    useEffect(() => {
        if (!showImage) return
        if (!files.length) return

        const startIndex = index ?? 0
        setCurrentPageIndex(clamp(startIndex + 1, 1, files.length))
    }, [index, files.length, showImage])

    const currentIndex = useMemo(() => currentPageIndex - 1, [currentPageIndex])

    useEffect(() => {
        if (!showImage) return
        const fetchImageData = async () => {
            if (!files.length) return
            if (currentIndex < 0 || currentIndex >= files.length) return

            const file = files[currentIndex]
            if (!file?.id) return

            try {
                const data = await getImage(plant, file.id)
                if (data.result !== FileResultEnum.Success) {
                    showInfo()
                    setCurrentImage(null)
                    setCurrentDescription(null)
                    return
                }
                setCurrentImage(data.file?.url ?? null)
                setCurrentDescription(file.title ?? null)
            } catch (error) {
                console.error('Error fetching image:', error)
            }
        }

        fetchImageData()
    }, [files, currentIndex, plant])

    return (
        <ImageContainer color="black" open={showImage} onClose={onClose} isDismissable>
            <Header>
                <HeaderLabel>Images {tag}</HeaderLabel>
                <CloseButton as="Link" onClick={onClose}>
                    <Icon data={close} size={24} style={{ color: '#007079' }} />
                </CloseButton>
            </Header>

            {currentImage && <ImageView src={currentImage} alt={`Image ${tag}`} />}

            {currentDescription && (
                <DescriptionContainer>
                    <Label>{currentDescription}</Label>
                </DescriptionContainer>
            )}

            <PaginationContainer>
                <Pagination
                    totalItems={files.length}
                    itemsPerPage={itemsPerPage}
                    defaultPage={index ? index + 1 : 1}
                    onChange={(_, page) => setCurrentPageIndex(page)}
                    withItemIndicator={false}
                />
            </PaginationContainer>
        </ImageContainer>
    )
}

const Label = styled.span`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    @media (max-width: 1024px) {
        &:hover {
            background: #243746;
            color: white;
        }
    }
`

const ImageView = styled.img`
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: 70vh;
`

const DescriptionContainer = styled.div`
    display: flex-start;
    justify-content: center;
    width: 100%;
`

const HeaderLabel = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #3d3d3d;
`

const ImageContainer = styled(Dialog)`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    display: flex;
    flex-direction: column;
    align-items: center;

    width: 550px;
    max-width: calc(100% - 32px);
    padding: 16px;

    @media (max-width: 1024px) {
        max-height: 90vh;
        overflow: hidden;
    }
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 0px;
`

const CloseButton = styled(Button)`
    ${baseFont}
    color: #007079;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        background: white;
    }

    @media (max-width: 1024px) {
        color: #007079;
        &:hover {
            background: white;
        }
    }
`

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
`

export default ImageDisplayer
